import { Box, Button, Flex, Icon, VStack } from '@chakra-ui/react';
import { Grid2X2Icon, MapIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { MOBILE_HEADER_HEIGHT } from '../Layouts/MobileHeader';
import { SimpleHeader } from '../Layouts/SimpleHeader';
import { MapboxGeocoder } from '../MapboxGeocoder/MapboxGeocoder';
import { MlsDisclaimers } from '../MlsDisclaimers/MlsDisclaimers';
import { SIDEBAR_WIDTH_COLLAPSED } from '../PageDetailLayout/PageDetailLayoutSidebar';
import { ExploreProvider, useExploreContext } from './ExploreContext';
import { ExploreMap } from './ExploreMap/ExploreMap';
import { MobileFilterDrawer } from './Filters/MobileFilterDrawer';
import { PropertyFilters } from './Filters/PropertyFilters';
import { ListingGrid } from './ListingGrid/ListingGrid';

export const MapSearchContent = ({
  showUnauthenticatedHeader = false,
}: {
  showUnauthenticatedHeader?: boolean;
}) => {
  const {
    listings,
    totalCount,
    mapRef,
    handleRetrieve,
    utmParams,
    disclaimers,
  } = useExploreContext();

  return (
    <>
      <VStack flexGrow={1} height="100%" spacing={0} width="100%">
        <Flex
          alignItems="center"
          left={`calc(${SIDEBAR_WIDTH_COLLAPSED} + 10px)`}
          position="absolute"
          pr={3}
          top="10px"
          width={{ base: 'calc(100% - 20px)', md: 'auto' }}
          zIndex="2"
        >
          <Flex direction="column" gap={2}>
            <Flex gap="10px">
              <Flex
                _focusWithin={{
                  border: '1px solid',
                  borderColor: 'whiteAlpha.600',
                }}
                background="bg.mainDark"
                border="1px solid"
                borderColor="whiteAlpha.200"
                borderRadius="8px"
                minWidth="350px"
              >
                <MapboxGeocoder
                  _focusVisible={{
                    border: 'none',
                  }}
                  bg="transparent"
                  border="none"
                  height="48px"
                  mapRef={mapRef}
                  ml={{ base: 2, md: 2 }}
                  onRetrieve={handleRetrieve}
                />
              </Flex>

              <Flex background="bg.mainDark" borderRadius="8px">
                <PropertyFilters />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <ExploreMap mapRef={mapRef} />
      </VStack>
      <VStack
        maxHeight="100%"
        maxWidth={{ base: '300px', xl: '580px' }}
        minHeight="100%"
        minWidth={{ base: 'unset', xl: '580px' }}
        overflowY="auto"
        px={0}
        spacing={0}
      >
        {showUnauthenticatedHeader && <SimpleHeader />}
        {/* <Flex alignItems="center" minHeight="56px" px={3} py={0} width="100%">
          <PremiumFilter
            value={filters?.premiumOnly || false}
            onChange={(e) => {
              updateFilters({ premiumOnly: e.target.checked });
            }}
          />
        </Flex> */}
        <ListingGrid
          listings={listings}
          totalCount={totalCount}
          utmParams={utmParams}
        />
        <MlsDisclaimers disclaimers={disclaimers} />
      </VStack>
    </>
  );
};

enum MobileMapView {
  MAP = 'map',
  LIST = 'list',
}
export const MobileMapSearchContent = () => {
  const [mobileView, setMobileView] = useState<MobileMapView>(
    MobileMapView.MAP
  );
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const drawerBtnRef = useRef<HTMLButtonElement>(null);

  const {
    listings,
    totalCount,
    mapRef,
    handleRetrieve,
    clearFilters,
    disclaimers,
  } = useExploreContext();

  return (
    <>
      <VStack flexGrow={1} height="100%" spacing={0} width="100%">
        <VStack
          backdropFilter="blur(2px)"
          background="linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.00) 100%)"
          position="absolute"
          pt={{ base: 0, md: 4 }}
          px={{ base: 3, md: 4 }}
          top={MOBILE_HEADER_HEIGHT}
          width="100%"
          zIndex="5"
        >
          <Box width="100%">
            <MapboxGeocoder mapRef={mapRef} onRetrieve={handleRetrieve} />
          </Box>
          <Flex
            alignItems="center"
            gap={2}
            justifyContent="space-between"
            width="100%"
          >
            <Button
              background="bg.mainDark"
              color="white"
              size="sm"
              onClick={() => setShowMobileFilters(true)}
            >
              Filters
            </Button>
            {/* <PremiumFilter
              backgroundColor="bg.mainDark"
              value={filters?.premiumOnly || false}
              onChange={(e) => {
                updateFilters({ premiumOnly: e.target.checked });
              }}
            /> */}
            <Button
              background="bg.mainDark"
              color="white"
              leftIcon={
                mobileView === MobileMapView.LIST ? (
                  <Icon as={MapIcon} />
                ) : (
                  <Icon as={Grid2X2Icon} />
                )
              }
              size="sm"
              onClick={() =>
                setMobileView((prev) =>
                  prev === MobileMapView.MAP
                    ? MobileMapView.LIST
                    : MobileMapView.MAP
                )
              }
            >
              {mobileView === MobileMapView.LIST ? 'Map' : 'List'}
            </Button>
          </Flex>
        </VStack>
        <ExploreMap
          mapRef={mapRef}
          visibility={mobileView === MobileMapView.MAP ? 'visible' : 'hidden'}
        />
        {mobileView === MobileMapView.LIST && (
          <Flex flexDirection="column" gap={1} height="100%">
            <ListingGrid listings={listings} totalCount={totalCount} />
            <MlsDisclaimers disclaimers={disclaimers} />
          </Flex>
        )}
      </VStack>
      <MobileFilterDrawer
        btnRef={drawerBtnRef}
        clearAll={() => {
          clearFilters();
          setShowMobileFilters(false);
        }}
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
      />
    </>
  );
};

export const MapSearch = ({
  showUnauthenticatedHeader = false,
}: {
  showUnauthenticatedHeader?: boolean;
}) => {
  const isMobile = useIsMobile(true);

  const page = (
    <ExploreProvider>
      {isMobile ? (
        <MobileMapSearchContent />
      ) : (
        <MapSearchContent
          showUnauthenticatedHeader={showUnauthenticatedHeader}
        />
      )}
    </ExploreProvider>
  );

  return page;
};
